import { Space, Text } from '@billy/design-system'
import { isValidElement } from 'react'
import { useTranslation } from 'react-i18next'

import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'

import { useLocale } from '../../../../../../../../hooks/useLocale'
import { formatDate } from '../../../../../../../../utils/date-fns'
import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import { DrawerSection } from '../DrawerSection'
import { OriginatorLink } from './OriginatorLink'
import * as Styled from './styles'

interface DetailsSectionProps {
  number?: string | null
  createdTime?: string | null
  paymentDate?: string | null
}

export const DetailsSection = ({ number, createdTime, paymentDate }: DetailsSectionProps) => {
  const {
    translationKey: type,
    creator,
    url: originatorUrl,
    originatorName,
    originatorType,
  } = useTransactionDrawerContext()
  const { t } = useTranslation()
  const { code } = useLocale()
  const createdDate = formatDate(createdTime, 'd MMM yyyy, HH:mm', code)
  const formattedPaymentDate = formatDate(paymentDate, 'd MMM yyyy', code)

  const originatorText = type && number ? `${type} ${number}` : originatorName || ''
  /* "Receipt" for bills or daybook transaction, invoice for invoices. Rest don't have this kind of field */
  const originatorLabel =
    originatorType === TransactionOriginatorType.Invoice || originatorType === TransactionOriginatorType.ExternalInvoice
      ? t('ledger.details.invoice_no')
      : t('ledger.details.receipt_no')

  const details = [
    { label: t('ledger.details.type'), value: type },
    ...(number ? [{ label: originatorLabel, value: number }] : []),
    ...(formattedPaymentDate ? [{ label: t('ledger.details.payment_date'), value: formattedPaymentDate }] : []),
    { label: t('ledger.details.created_by'), value: creator },
    { label: t('ledger.details.created'), value: createdDate },
    ...(originatorUrl
      ? [
          {
            label: t('ledger.details.origin'),
            value: <OriginatorLink text={originatorText} url={originatorUrl} />,
          },
        ]
      : []),
  ]

  return (
    <DrawerSection title={t('ledger.details')} gap={false}>
      <Space size="s8" />
      {details.map((detail, index) => (
        <Styled.DetailLine key={index}>
          <Text colorVariant="disable">{detail.label}</Text>
          {isValidElement(detail.value) ? detail.value : <Text alignment="right">{detail.value}</Text>}
        </Styled.DetailLine>
      ))}
    </DrawerSection>
  )
}
